import { DOC_TYPE } from 'definitions/DocType';
import { AuthType } from 'mainpage/knowledgebase/dropdown/DropDownAuth';
import { KV } from 'models/IfKV';
import { IfPageInfoDlCenter } from 'models/IfPageInfo';
import { IfTagListEntity } from 'models/IfTagListEntity';
import { ICountryCode } from 'repositories/AccountService';
import svcs from 'services';
import { create } from 'zustand';
import { IfDlCenterReq, IfDlCenterRes } from './dlcenter-model';
import { CtgryTree, ICtgryTree } from './leftpanel/ctgry/useZstdProdCtgry';
import { LANG } from './leftpanel/useSiteLang';

export const getSelPath = (path: string, sel: ICtgryTree) => {
  if (!sel || !sel?.self) return path;
  if (path) return getSelPath(sel.self.name + ' > ' + path, sel.parent);
  return getSelPath(sel.self.name + path, sel.parent);
};

export type DocTypeInfo = {
  type: DOC_TYPE;
  label: string;
  num: number;
};

export const ctryTreeFilter = (item: ICtgryTree, lowerSrch: string) => {
  const name = item.self.name.toLowerCase();
  const models = item.self.lowerModels;
  if (name.includes(lowerSrch)) return true;

  if (models.some((model) => model.includes(lowerSrch))) return true;

  if (item.childs.length > 0) {
    return item.childs.some((ele) => ctryTreeFilter(ele, lowerSrch));
  }
  return false;
};

export type SelMenu = 'ctry' | 'docType' | 'fileLang' | 'siteLang' | 'tag' | '';

export type ZstdDlCenter = {
  selProdCtgry: ICtgryTree;
  setSelProdCtgry: (v: ICtgryTree) => void;
  selDocTypes: { [key: string]: DocTypeInfo };
  setSelDocTypes: (v: { [key: string]: DocTypeInfo }) => void;
  selFileLang: { [key: string]: ICountryCode };
  setSelFileLang: (v: { [key: string]: ICountryCode }) => void;
  selSiteLang: Set<LANG | undefined | ''>;
  setSelSiteLang: (v: Set<LANG | undefined | ''>) => void;
  selTags: { [key: string]: IfTagListEntity };
  setSelTags: (v: { [key: string]: IfTagListEntity }) => void;
  selPeriod: string;
  setSelPeriod: (v: string) => void;
  selOrder: string;
  isOrderManual: boolean;
  setSelOrder: (v: string, manual?: boolean) => void;
  resetOrder: () => void;
  clearOrderManual: () => void;
  pageSize: number;
  setPageSize: (v: number) => void;
  pageNo: number;
  setPageNo: (v: number) => void;
  showMenu: boolean;
  setShowMenu: (v: boolean) => void;
  floatMenu: boolean;
  setFloatMenu: (v: boolean) => void;
  selAuth: KV<AuthType, string>;
  setSelAuth: (v: KV<AuthType, string>) => void;

  selDocId: number;
  setSelDocId: (v: number) => void;

  // panelTop: number;
  // setPanelTop: (v: number) => void;
  // cntsBottom: number;
  // setCntsBottom: (v: number) => void;

  docCnt: number;
  setDocCnt: (v: number) => void;
  docTypesCnt: { [key in DOC_TYPE]?: number };
  setDocTypesCnt: (v: { [key in DOC_TYPE]?: number }) => void;

  selMenu: SelMenu;
  setSelMenu: (v: SelMenu) => void;

  isCntsIntersecting: boolean;
  setIsCntsIntersecting: (v: boolean) => void;

  triggerSrchNo: number;
  triggerSrch: () => void;

  keywords: string[];
  setKeywords: (v: string[]) => void;

  list: IfDlCenterRes[];
  setList: (v: IfDlCenterRes[] | ((prev: IfDlCenterRes[]) => IfDlCenterRes[])) => void;

  data: IfPageInfoDlCenter<IfDlCenterRes> | undefined;
  setData: (v: IfPageInfoDlCenter<IfDlCenterRes> | undefined) => void;

  debonceedPSaved: IfDlCenterReq | undefined;
  setDebonceedPSaved: (v: IfDlCenterReq | undefined) => void;
  bodyScrollY: number;
  setBodyScrollY: (v: number) => void;
};

export const useZstdDlCenter = create<ZstdDlCenter>((set) => ({
  selProdCtgry: new CtgryTree(),
  setSelProdCtgry: (v: ICtgryTree) => set({ selProdCtgry: v }),
  selDocTypes: {},
  setSelDocTypes: (v: { [key: string]: DocTypeInfo }) => set({ selDocTypes: v }),
  selFileLang: {},
  setSelFileLang: (v: { [key: string]: ICountryCode }) => set({ selFileLang: v }),
  selSiteLang: new Set<LANG | undefined | ''>([svcs.svcLang.lang() as LANG]),
  setSelSiteLang: (v: Set<LANG | undefined | ''>) => set({ selSiteLang: v }),
  selTags: {},
  setSelTags: (v: { [key: string]: IfTagListEntity }) => set({ selTags: v }),
  pageSize: 20,
  setPageSize: (v: number) => set({ pageSize: v }),
  pageNo: 1,
  setPageNo: (v: number) => set({ pageNo: v }),
  selPeriod: '',
  setSelPeriod: (v: string) => set({ selPeriod: v }),
  selOrder: 'UPDATE',
  isOrderManual: false,
  setSelOrder: (v: string, manual = false) =>
    set((state) => ({
      selOrder: v,
      isOrderManual: manual ? true : state.isOrderManual,
    })),
  resetOrder: () => set({ selOrder: 'UPDATE', isOrderManual: false }),
  clearOrderManual: () => set({ isOrderManual: false }),
  showMenu: true,
  setShowMenu: (v: boolean) => set({ showMenu: v }),
  floatMenu: false,
  setFloatMenu: (v: boolean) => set({ floatMenu: v }),
  //panelTop: 0,
  //setPanelTop: (v: number) => set({panelTop: v}),
  //cntsBottom: 0,
  //setCntsBottom: (v: number) => set({cntsBottom: v}),
  docCnt: 0,
  setDocCnt: (v: number) => set({ docCnt: v }),
  docTypesCnt: {},
  setDocTypesCnt: (v: { [key in DOC_TYPE]?: number }) => set({ docTypesCnt: v }),
  selMenu: '',
  setSelMenu: (v: SelMenu) => set({ selMenu: v }),
  isCntsIntersecting: false,
  setIsCntsIntersecting: (v: boolean) => set({ isCntsIntersecting: v }),
  selAuth: { key: undefined, val: undefined },
  setSelAuth: (v: KV<AuthType, string>) => set({ selAuth: v }),

  selDocId: undefined,
  setSelDocId: (v: number) => set({ selDocId: v }),
  triggerSrchNo: 0,
  triggerSrch: () => set((state) => ({ triggerSrchNo: state.triggerSrchNo + 1 })),

  keywords: [],
  setKeywords: (v: string[]) => set({ keywords: v }),

  list: [],
  setList: (v: IfDlCenterRes[] | ((prev: IfDlCenterRes[]) => IfDlCenterRes[])) =>
    set((state) => ({ list: typeof v === 'function' ? v(state.list) : v })),

  data: undefined,
  setData: (v: IfPageInfoDlCenter<IfDlCenterRes> | undefined) => set({ data: v }),

  debonceedPSaved: undefined,
  setDebonceedPSaved: (v: IfDlCenterReq | undefined) => set({ debonceedPSaved: v }),
  bodyScrollY: undefined,
  setBodyScrollY: (v: number) => set({ bodyScrollY: v }),
}));
